import { FloatingHeader } from "./floating-header";
import { NordexFooter, NordexBrandFooter } from "@nordex/ui";
import type { NordexFooterConfig } from "@nordex/ui";
import { DEFAULT_LOCALE } from "@nordex/i18n";

// -------------------------------------------------------------------
// Section data (still exported – used by the support index page)
// -------------------------------------------------------------------
type SupportSectionItem = {
  label: string;
  href: string;
  external?: boolean;
};

type SupportSection = {
  title: string;
  items: SupportSectionItem[];
};

const STATUS_URL =
  import.meta.env.VITE_STATUS_URL || "https://status.nordex.trade";

const SUPPORT_SECTIONS: SupportSection[] = [
  {
    title: "Support",
    items: [
      { label: "Contact Us", href: "/contact" },
      { label: "FAQ", href: "/faq" },
      { label: "Feedback", href: "/feedback" },
      { label: "API Access", href: "/api-access" },
    ],
  },
  {
    title: "Company",
    items: [
      { label: "About", href: "/about" },
      { label: "Careers", href: "/careers" },
      { label: "Media Kit", href: "/media-kit" },
      { label: "Status", href: STATUS_URL, external: true },
    ],
  },
  {
    title: "Legal",
    items: [
      { label: "Terms of Use", href: "/terms" },
      { label: "Privacy Policy", href: "/privacy" },
    ],
  },
];

// -------------------------------------------------------------------
// Footer config from env vars
// -------------------------------------------------------------------
const basePath = import.meta.env.BASE_URL || "/docs/";
const SUPPORT_URL = import.meta.env.VITE_SUPPORT_URL || "/support/";
const DOCS_URL = import.meta.env.VITE_DOCS_URL || "/docs/";

const footerConfig: NordexFooterConfig = {
  appUrl: import.meta.env.VITE_FRONTEND_URL || "https://app.nordex.trade",
  docsUrl: DOCS_URL,
  supportUrl: SUPPORT_URL,
  iosAppUrl:
    import.meta.env.VITE_IOS_APP_URL ||
    "https://testflight.apple.com/join/VnQeB4AZ",
  googlePlayUrl: import.meta.env.VITE_GOOGLE_PLAY_URL || "",
  apkDownloadUrl:
    import.meta.env.VITE_APK_DOWNLOAD_URL ||
    "https://bcsnkehdffboctvgpwye.supabase.co/storage/v1/object/public/app-releases/android/nordex-android-v0.1.0-dev.apk",
  basePath,
  lightLogo: "brand/Nordex_Logo_Color_Black.svg",
  darkLogo: "brand/Nordex_Logo_Color_White.svg",
  wordmarkBlack: "brand/Nordex_Logo_Black.svg",
  wordmarkWhite: "brand/Nordex_Logo_White.svg",
  partnersUrl: (import.meta.env.VITE_LANDING_URL || "/landing/").replace(/\/?$/, '/') + `${DEFAULT_LOCALE}/partners`,
  investorsUrl: (import.meta.env.VITE_LANDING_URL || "/landing/").replace(/\/?$/, '/') + `${DEFAULT_LOCALE}/investors`,
};

// -------------------------------------------------------------------
// SupportLayout – full-width (no sidebar), with shared footer
// -------------------------------------------------------------------
type SupportLayoutProps = {
  children: React.ReactNode;
  /** Override the inner max-width (default: max-w-4xl). Pass e.g. "max-w-6xl" for wider pages like Media Kit. */
  maxWidth?: string;
};

export function SupportLayout({
  children,
  maxWidth = "max-w-4xl",
}: SupportLayoutProps) {
  return (
    <div className="min-h-screen flex flex-col bg-dot-pattern">
      {/* Floating Pill Menu */}
      <FloatingHeader />

      {/* Spacer for fixed header */}
      <div className="h-[85px]" />

      {/* Main content card – full width within container, no sidebar */}
      <div className="max-w-[1340px] mx-auto px-2 sm:px-4 lg:px-6 pb-6 flex-1 w-full">
        <main className="docs-card rounded-xl border border-border">
          <div className="py-10 px-4 md:px-10 lg:px-14">
            <div className={`${maxWidth} mx-auto docs-content`}>{children}</div>
          </div>
        </main>
      </div>

      {/* Shared footer */}
      <NordexFooter config={footerConfig} />
      <NordexBrandFooter config={footerConfig} />
    </div>
  );
}

export { SUPPORT_SECTIONS };
export type { SupportSection, SupportSectionItem };
